import { SmallAddIcon } from "@chakra-ui/icons";
import {
  Button,
  Card,
  CardBody,
  HStack,
  MenuDivider,
  MenuItem,
  Modal,
  ModalContent,
  ModalOverlay,
  Text,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { useStytch } from "@stytch/react";
import { AuthFactorInfos, AuthFactorInstance } from "features/auth/AuthFactor";
import { AuthFactorSetup } from "features/auth/factor/AuthFactorSetupPage";
import { AuthFactorRecoverySetup } from "features/auth/factor/recovery/AuthFactorRecoverySetup";
import {
  getStytchErrorMessage,
  useStytchUserAuthFactorInstances,
} from "features/auth/StytchUtils";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { CardMenuButton } from "shared/components/card/CardMenuButton";
import { useConfirmationDialog } from "shared/components/modal/confirm/ConfirmationDialog";
import { DeleteConfirmationDialog } from "shared/components/modal/DeleteConfirmationDialog";
import { useToast } from "shared/components/toast/useToast";

interface AuthFactorDeleteArgs {
  authFactor: AuthFactorInstance;
}

export const AccountSecurityMfa: React.FC = () => {
  const { showErrorToast } = useToast();
  const { t } = useTranslation(["common", "account", "auth"]);

  const stytchClient = useStytch();
  const authFactors = useStytchUserAuthFactorInstances();

  const {
    isOpen: isAuthSetupOpen,
    onOpen: openAuthSetup,
    onClose: closeAuthSetup,
  } = useDisclosure();

  const deleteAuthFactor = useConfirmationDialog(
    DeleteConfirmationDialog as DeleteConfirmationDialog<AuthFactorDeleteArgs>,
    {
      title: t("account:security.mfa.deleteConfirmation.title"),
      description: t("account:security.mfa.deleteConfirmation.description"),
      successMessage: t("account:security.mfa.deleteConfirmation.success"),
      performDelete: async ({ authFactor }) => {
        try {
          switch (authFactor.type) {
            case "totp":
              await stytchClient.user.deleteTOTP(authFactor.data.totp_id);
              break;
            case "otp":
              await stytchClient.user.deletePhoneNumber(
                authFactor.data.phone_id,
              );
              break;
            case "webauthn":
              await stytchClient.user.deleteWebauthnRegistration(
                authFactor.data.webauthn_registration_id,
              );
              break;
          }
        } catch (e) {
          showErrorToast(getStytchErrorMessage(t, e));
        }
      },
    },
  );

  return (
    <>
      <VStack spacing="10px" alignItems="stretch" mb="10px">
        {authFactors.map((authFactor) => (
          <AuthFactorInfoView
            key={authFactor.id}
            authFactor={authFactor}
            onDelete={deleteAuthFactor}
          />
        ))}
      </VStack>
      <Button
        colorScheme="secondary"
        leftIcon={<SmallAddIcon />}
        onClick={openAuthSetup}
      >
        {t("auth:addAnotherMethod")}
      </Button>
      <Modal
        isOpen={isAuthSetupOpen}
        onClose={closeAuthSetup}
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent>
          <AuthFactorSetup
            onSuccess={closeAuthSetup}
            onCancel={closeAuthSetup}
            hideRememberOption={true}
          />
        </ModalContent>
      </Modal>
    </>
  );
};

const AuthFactorInfoView: React.FC<{
  authFactor: AuthFactorInstance;
  onDelete: (args: AuthFactorDeleteArgs) => void;
}> = ({ authFactor, onDelete }) => {
  const { t } = useTranslation(["common", "auth"]);
  const { showErrorToast } = useToast();
  const stytchClient = useStytch();

  const [recoveryCodes, setRecoveryCodes] = useState<string[]>();
  const {
    isOpen: isRecoverySetupOpen,
    onOpen: openRecoverySetup,
    onClose: closeRecoverySetup,
  } = useDisclosure();

  const authFactorInfo = AuthFactorInfos[authFactor.type];
  return (
    <Card size="sm">
      <CardBody>
        <HStack flex={1} spacing="16px">
          {React.createElement(authFactorInfo.icon, {
            width: "24px",
            height: "24px",
          })}
          <VStack alignItems="flex-start" spacing={0}>
            <Text textStyle="bodyBold">{authFactorInfo.name}</Text>
            {authFactor.name && <Text color="gray.500">{authFactor.name}</Text>}
          </VStack>
        </HStack>
        <CardMenuButton>
          {authFactor.type === "totp" && (
            <>
              <MenuItem
                onClick={async () => {
                  try {
                    const resp = await stytchClient.totps.recoveryCodes();
                    // FIXME: Incorrect type definition
                    const respFixed = resp as unknown as {
                      totps: {
                        totp_id: string;
                        verified: boolean;
                        recovery_codes: string[];
                      }[];
                    };
                    const totp = respFixed.totps.find(
                      (it) => it.totp_id === authFactor.data.totp_id,
                    );
                    setRecoveryCodes(totp?.recovery_codes);
                    openRecoverySetup();
                  } catch (e) {
                    showErrorToast(getStytchErrorMessage(t, e));
                  }
                }}
              >
                {t("auth:recoveryCodes")}
              </MenuItem>
              <MenuDivider />
            </>
          )}
          <MenuItem
            color="error.500"
            _focus={{ bg: "error.25" }}
            onClick={() => {
              onDelete({ authFactor });
            }}
          >
            {t("common:delete")}
          </MenuItem>
        </CardMenuButton>
        {recoveryCodes && (
          <Modal
            isOpen={isRecoverySetupOpen}
            onClose={closeRecoverySetup}
            scrollBehavior="inside"
          >
            <ModalOverlay />
            <ModalContent>
              <AuthFactorRecoverySetup
                onCancel={closeRecoverySetup}
                recoveryCodes={recoveryCodes}
              />
            </ModalContent>
          </Modal>
        )}
      </CardBody>
    </Card>
  );
};
